import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { useStateContext } from '../../contexts/ContextProvider';
import axiosClient from '../../axios-client';

// Function to generate a color from a palette of 30 colors based on index
const getDynamicColor = (index) => {
  const colorPalette = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-yellow-100 text-yellow-800',
    'bg-purple-100 text-purple-800',
    'bg-red-100 text-red-800',
    'bg-indigo-100 text-indigo-800',
    'bg-teal-100 text-teal-800',
    'bg-pink-100 text-pink-800',
    'bg-orange-100 text-orange-800',
    'bg-cyan-100 text-cyan-800',
    'bg-rose-100 text-rose-800',
    'bg-emerald-100 text-emerald-800',
    'bg-amber-100 text-amber-800',
    'bg-violet-100 text-violet-800',
    'bg-fuchsia-100 text-fuchsia-800',
    'bg-lime-100 text-lime-800',
    'bg-sky-100 text-sky-800',
    'bg-stone-100 text-stone-800',
    'bg-neutral-100 text-neutral-800',
    'bg-zinc-100 text-zinc-800',
    'bg-gray-100 text-gray-800',
    'bg-slate-100 text-slate-800',
    'bg-blue-200 text-blue-900',
    'bg-green-200 text-green-900',
    'bg-yellow-200 text-yellow-900',
    'bg-purple-200 text-purple-900',
    'bg-red-200 text-red-900',
    'bg-indigo-200 text-indigo-900',
    'bg-teal-200 text-teal-900',
    'bg-pink-200 text-pink-900',
  ];
  return colorPalette[index % colorPalette.length];
};

const EleveEmploie = ({ student, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [emploies, setEmploies] = useState([]);
  const [errors, setErrors] = useState(null);
  const [subjectColors, setSubjectColors] = useState({});
  const { token, setToken } = useStateContext();
  const navigate = useNavigate();

  const getEmploies = async () => {
    setLoading(true);
    try {
      const { data } = await axiosClient.get(`/eleveEmploie/${student.id}`);
      setEmploies(data.emploies);

      // Dynamically assign colors to unique subjects
      const uniqueSubjects = [...new Set(data.emploies.map(emploi => emploi.matiere.matiere.libelle))];
      const newSubjectColors = uniqueSubjects.reduce((acc, subject, index) => ({
        ...acc,
        [subject]: getDynamicColor(index),
      }), {});
      setSubjectColors(newSubjectColors);

      setErrors(null);
    } catch (err) {
      const response = err.response;
      if (response?.status === 401) {
        setToken(null);
      } else if (response?.status === 422) {
        setErrors(response.data.errors);
      } else if (response?.status === 403) {
        navigate('/403');
      } else if (response?.status === 404) {
        navigate('/404');
      } else {
        navigate('/500');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEmploies();
  }, [student.id]);

  // Group emploies by day for grid layout, with Dimanche after Samedi
  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const groupedEmploies = days.map(day => ({
    day,
    sessions: emploies.filter(emploi => emploi.jour.libelleFr === day),
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-opacity duration-300">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6 md:p-8 animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <FaCalendarAlt className="text-blue-600 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-800">{student.eleve} - Emploi du temps</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-red-600 transition-colors duration-200"
            aria-label="Fermer"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Errors */}
        {errors && (
          <div className="mb-6 space-y-2">
            {Object.keys(errors).map((key) => (
              <div key={key} className="bg-red-100 text-red-800 p-3 rounded-lg">
                {errors[key][0]}
              </div>
            ))}
          </div>
        )}

        {/* Timetable Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
          </div>
        ) : emploies.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-lg">
            Aucun emploi du temps trouvé.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedEmploies.map(({ day, sessions }) => (
              <div key={day} className="bg-gray-50 rounded-lg p-4 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex justify-between">
                  <span>{day}</span>
                  <span className="text-gray-500">
                    {sessions[0]?.jour.libelleAr || ''}
                  </span>
                </h3>
                {sessions.length === 0 ? (
                  <p className="text-gray-400 italic">Aucun cours prévu</p>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((emploi) => (
                      <div
                        key={emploi.id}
                        className={`p-3 rounded-lg ${subjectColors[emploi.matiere.matiere.libelle] || 'bg-gray-100 text-gray-800'} transition-transform duration-200 hover:scale-[1.02]`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{emploi.matiere.matiere.libelle}</p>
                            <p className="text-sm text-gray-600">{emploi.enseignant.nomTel}</p>
                          </div>
                          <div className="text-sm font-semibold">
                            {emploi.heureDebut} - {emploi.heureFin}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

EleveEmploie.propTypes = {
  student: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eleve: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

EleveEmploie.defaultProps = {
  student: {
    id: '',
    eleve: 'Unknown Student',
  },
};

export default EleveEmploie;